// @refresh reload
import "solid-devtools/setup";

import Log from "@repo/utils/Log";
import Env from "@repo/utils-client/Env";
import Platform from "@repo/utils-client/Platform";

if (
  Platform.isClient &&
  new URLSearchParams(window.location.search).has("DEBUG")
)
  Log.config.level = "debug";
Env.config.debug =
  import.meta.env.APP_ENV !== "production" || Log.config.level === "debug";
Log.config.enabled = Env.config.debug;

import { ComponentProps, ErrorBoundary, JSX, onMount } from "solid-js";
import { createEffect, Suspense } from "solid-js";
import { MetaProvider, Title } from "@solidjs/meta";
import { Router, RouteSectionProps } from "@solidjs/router";
import { captureException } from "@sentry/solid";
import { createMediaQuery } from "@solid-primitives/media";

import t from "@repo/localization/string.macro";
import LoadableButton from "@repo/ui/buttons/LoadableButton";
import ShowMore from "@repo/ui/containers/ShowMore";
import { useError } from "@repo/ui/error/ErrorProvider";
import GlobalRoot from "@repo/ui/global/GlobalRoot";
import Constraints from "@repo/ui/layout/Constraints";
import Spinner from "@repo/ui/loaders/Spinner";
import AppRouter from "@repo/utils-solid/AppRouter";

import analytics from "~/analytics/client";
import { track } from "~/analytics/client";
import { OverlaysProvider } from "~/overlays";
import { useUser } from "~/state/features/user";

declare namespace SharedRootRouter {
  type Props = {
    root?: (props: RouteSectionProps) => JSX.Element;
    children: ComponentProps<typeof Router>["children"];
  };
}

function SharedRootRouter({ root, children }: D<SharedRootRouter.Props>) {
  const isSmall = createMediaQuery("(max-width: 500px)", true);
  return (
    <ErrorBoundary
      fallback={(e) => {
        // TODO: Router currently bugs out when logging out (invalidating), then
        // routing to 404 page, reload when this happens. Need to submit github
        // issue with reproduction
        if (e.message.includes("path") || e.message.includes("match")) {
          onMount(() => {
            // Reloading immediately loads old path, must delay
            setTimeout(() => {
              window.location.reload();
            });
          });

          return <Spinner.Common />;
        }

        captureException(e);
        throw e;
      }}
    >
      <ShowMore.PropsProvider
        text={(show) => (show ? t`Show less` : t`Show more`)}
      >
        <LoadableButton.PropsProvider
          text={{
            pending: t`Loading...`,
            success: t`Success`,
          }}
        >
          <Constraints.PropsProvider
            maxWidth="1000px"
            class={isSmall() ? "p-1" : ""}
          >
            <MetaProvider>
              <Title>Community | Kikoff</Title>

              <Router
                root={(props) => {
                  AppRouter.register();

                  createEffect((from?: string) => {
                    track("view page", { initial: !from, from: from! });
                    return AppRouter.pathname;
                  });

                  return (
                    <Suspense>
                      {/* GlobalRoot must be inside of Router since overlays depend on Route context  */}
                      <GlobalRoot OverlaysProvider={OverlaysProvider}>
                        {(() => {
                          const [user, setUser] = useUser();
                          const error = useError();

                          analytics.context = { user };

                          onMount(() => {
                            setUser.init().catch((e) => {
                              error.capture(e);
                            });
                          });

                          return <>{root?.(props) || props.children}</>;
                        })()}
                      </GlobalRoot>
                    </Suspense>
                  );
                }}
              >
                {children}
              </Router>
            </MetaProvider>
          </Constraints.PropsProvider>
        </LoadableButton.PropsProvider>
      </ShowMore.PropsProvider>
    </ErrorBoundary>
  );
}

export default SharedRootRouter;
